import React, { useState, useEffect } from 'react'
import { withStyles } from '@material-ui/core/styles'
import {
  Typography,
  TextField,
  Button,
  IconButton,
  Box,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/DeleteOutline'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import LockIcon from '@material-ui/icons/LockOutlined'
import StorageIcon from '@material-ui/icons/Storage'
import EditIcon from '@material-ui/icons/EditOutlined'
import RefreshIcon from '@material-ui/icons/Refresh'
import {
  headlessStoreApiKeys,
  headlessApiKeyStatus,
  headlessDeleteApiKey,
  headlessGetPrompts,
  headlessStorePrompts,
  headlessResetPrompt,
} from 'src/api/headless'
import styles from './styles'

const PROMPT_MIN_LENGTH = 30

const KeyRow = ({ label, isSet, value, onChange, onRemove, placeholder, classes, loading }) => (
  <Box mb={3}>
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
      <Typography style={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>
        {label}
      </Typography>
      {isSet && (
        <div className={classes.statusBadge}>
          <CheckCircleIcon style={{ color: '#10B981', fontSize: '16px' }} />
          <Typography className={classes.activeText}>ACTIVE</Typography>
          <Tooltip title="Remove Key">
            <IconButton size="small" onClick={onRemove} style={{ marginLeft: '4px', padding: '2px' }}>
              <DeleteIcon style={{ fontSize: '16px', color: '#EF4444' }} />
            </IconButton>
          </Tooltip>
        </div>
      )}
    </Box>
    <TextField
      fullWidth
      type="password"
      variant="outlined"
      size="small"
      placeholder={isSet ? '••••••••••••••••••••••••••••' : placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      className={classes.input}
      disabled={loading}
    />
  </Box>
)

const PromptRow = ({
  label,
  description,
  value,
  onChange,
  rows,
  isCustom,
  defaultValue,
  onSave,
  onReset,
  loading,
  classes,
}) => {
  const trimmedLen = (value || '').trim().length
  const tooShort = trimmedLen < PROMPT_MIN_LENGTH
  const baseline = isCustom ? value : defaultValue
  // Disable Save when nothing has changed vs. what's currently persisted, or
  // when the user dropped below the 30-char minimum.
  const unchanged = value === baseline
  return (
    <Box mb={4}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography style={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>
          {label}
        </Typography>
        {isCustom && (
          <div className={classes.statusBadge}>
            <CheckCircleIcon style={{ color: '#10B981', fontSize: '16px' }} />
            <Typography className={classes.activeText}>CUSTOM</Typography>
          </div>
        )}
      </Box>
      {description && (
        <Typography style={{ fontSize: '12px', color: '#64748B', marginBottom: '8px', lineHeight: 1.5 }}>
          {description}
        </Typography>
      )}
      <TextField
        fullWidth
        multiline
        rows={rows}
        variant="outlined"
        size="small"
        value={value}
        onChange={e => onChange(e.target.value)}
        className={classes.input}
        disabled={loading}
        error={tooShort && trimmedLen > 0}
        helperText={
          tooShort && trimmedLen > 0
            ? `Minimum ${PROMPT_MIN_LENGTH} characters (currently ${trimmedLen}).`
            : `${trimmedLen} characters`
        }
      />
      <Box display="flex" mt={1.5} style={{ gap: '12px' }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={loading || tooShort || unchanged}
          onClick={onSave}
          className={classes.button}
          style={{ padding: '6px 20px' }}
        >
          {loading ? <CircularProgress size={16} color="inherit" /> : 'Save'}
        </Button>
        <Tooltip title={isCustom ? 'Restore the built-in default' : 'Already using the default'}>
          <span>
            <Button
              variant="outlined"
              size="small"
              disabled={loading || !isCustom}
              onClick={onReset}
              className={classes.button}
              style={{ padding: '6px 20px' }}
              startIcon={<RefreshIcon style={{ fontSize: '16px' }} />}
            >
              Reset to default
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  )
}

const SettingsPanel = ({
  classes,
  googleDrive,
  generateVideo: initialGenerateVideo,
  onGenerateVideoChange,
  backupIntroVideosToDrive,
  onBackupIntroVideosToDriveChange,
  avatarId,
  onAvatarIdChange,
}) => {
  const [geminiKey, setGeminiKey] = useState('')
  const [mistralKey, setMistralKey] = useState('')
  const [replicateKey, setReplicateKey] = useState('')
  const [heygenKey, setHeygenKey] = useState('')
  const [generateVideo, setGenerateVideo] = useState(initialGenerateVideo ?? false)
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(false)

  // Custom prompt overrides (per-user). `defaults` is whatever the worker
  // seeded into mongo at boot. `overrides` is what this user has saved
  // (null ⇒ using the default). The textarea state mirrors the effective
  // value (override-or-default) so users can edit either case.
  const [promptDefaults, setPromptDefaults] = useState({
    video_script_rules: '',
    heygen_prompt: '',
  })
  const [promptOverrides, setPromptOverrides] = useState({
    video_script_rules: null,
    heygen_prompt: null,
  })
  const [scriptRulesValue, setScriptRulesValue] = useState('')
  const [heygenPromptValue, setHeygenPromptValue] = useState('')
  const [promptLoading, setPromptLoading] = useState({ script: false, heygen: false })
  const [promptError, setPromptError] = useState(null)

  // Sync local state when the parent's persisted value changes (e.g. on initial mount)
  useEffect(() => {
    setGenerateVideo(initialGenerateVideo ?? false)
  }, [initialGenerateVideo])

  const fetchStatus = async () => {
    try {
      const res = await headlessApiKeyStatus()
      // Based on user feedback, response is { keys: { google_api_key: true, ... } }
      const data = res?.data || res
      setStatus(data?.keys || data)
    } catch (e) {
      console.error('Failed to fetch BYOK status:', e)
    }
  }

  useEffect(() => {
    fetchStatus()
  }, [])

  const fetchPrompts = async () => {
    try {
      const res = await headlessGetPrompts()
      const data = res?.data || res
      const defaults = data?.defaults || { video_script_rules: '', heygen_prompt: '' }
      const overrides = data?.overrides || { video_script_rules: null, heygen_prompt: null }
      setPromptDefaults(defaults)
      setPromptOverrides(overrides)
      setScriptRulesValue(overrides.video_script_rules ?? defaults.video_script_rules ?? '')
      setHeygenPromptValue(overrides.heygen_prompt ?? defaults.heygen_prompt ?? '')
    } catch (e) {
      console.error('Failed to fetch custom prompts:', e)
    }
  }

  useEffect(() => {
    fetchPrompts()
  }, [])

  const handleSavePrompt = async (field, value, loadingKey) => {
    setPromptLoading(prev => ({ ...prev, [loadingKey]: true }))
    setPromptError(null)
    try {
      await headlessStorePrompts({ [field]: value })
      await fetchPrompts()
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to save prompt'
      setPromptError(msg)
      console.error(`Failed to save prompt ${field}:`, e)
    } finally {
      setPromptLoading(prev => ({ ...prev, [loadingKey]: false }))
    }
  }

  const handleResetPrompt = async (field, loadingKey) => {
    setPromptLoading(prev => ({ ...prev, [loadingKey]: true }))
    setPromptError(null)
    try {
      await headlessResetPrompt(field)
      await fetchPrompts()
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to reset prompt'
      setPromptError(msg)
      console.error(`Failed to reset prompt ${field}:`, e)
    } finally {
      setPromptLoading(prev => ({ ...prev, [loadingKey]: false }))
    }
  }

  const handleSaveKeys = async () => {
    setLoading(true)
    try {
      const keys = {}
      if (geminiKey) keys.google_api_key = geminiKey
      if (mistralKey) keys.mistral_api_key = mistralKey
      if (replicateKey) keys.replicate_api_key = replicateKey
      if (heygenKey) keys.heygen_api_key = heygenKey
      
      await headlessStoreApiKeys(keys)
      setGeminiKey('')
      setMistralKey('')
      setReplicateKey('')
      setHeygenKey('')
      await fetchStatus()
    } catch (e) {
      console.error('Failed to save keys:', e)
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveKey = async type => {
    try {
      await headlessDeleteApiKey(type)
      await fetchStatus()
    } catch (e) {
      console.error(`Failed to remove key ${type}:`, e)
    }
  }

  // Helper to check if a key is set in the status object
  const checkIsSet = (keyName) => {
    if (!status) return false
    return status[keyName] === true || (typeof status[keyName] === 'string' && status[keyName].length > 0)
  }

  const handleToggleVideo = (e) => {
    const val = e.target.checked
    setGenerateVideo(val)
    if (onGenerateVideoChange) {
      onGenerateVideoChange(val)
    }
  }

  const handleToggleBackup = (e) => {
    const val = e.target.checked
    if (onBackupIntroVideosToDriveChange) {
      onBackupIntroVideosToDriveChange(val)
    }
  }

  return (
    <div className={classes.root}>
      {/* Google Drive Status */}
      <div className={classes.section}>
        <Box display="flex" alignItems="center" mb={2.5}>
          <StorageIcon style={{ color: '#3B82F6', marginRight: '16px' }} />
          <Typography style={{ fontSize: '16px', fontWeight: 700, color: '#1E293B' }}>
            Google Drive Connection
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="flex-start">
            {googleDrive.connected ? (
              <CheckCircleIcon style={{ color: '#10B981', fontSize: '28px', marginRight: '20px', marginTop: '2px' }} />
            ) : (
              <div style={{ width: '28px', height: '28px', borderRadius: '50%', border: '2px solid #CBD5E1', marginRight: '20px', marginTop: '2px' }} />
            )}
            <div>
              <Typography style={{ fontSize: '16px', fontWeight: 700, color: '#1E293B', lineHeight: 1.2 }}>
                {googleDrive.connected ? 'Connected' : 'Not Connected'}
              </Typography>
              <Typography style={{ fontSize: '14px', color: '#64748B', marginTop: '4px' }}>
                {googleDrive.connected
                  ? 'Access to your Google Drive is active.'
                  : 'Connect your Drive to start browsing PDF files.'}
              </Typography>
            </div>
          </Box>
          {googleDrive.connected ? (
            <Button
              size="small"
              onClick={googleDrive.disconnect}
              className={classes.button}
              style={{ color: '#EF4444', fontWeight: 700 }}
            >
              Disconnect
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={googleDrive.connect}
              className={classes.button}
              style={{ padding: '8px 24px' }}
            >
              Connect Drive
            </Button>
          )}
        </Box>
      </div>

      {/* BYOK Settings */}
      <div className={classes.section}>
        <Box display="flex" alignItems="center" mb={2.5}>
          <LockIcon style={{ color: '#3B82F6', marginRight: '16px' }} />
          <Typography style={{ fontSize: '16px', fontWeight: 700, color: '#1E293B' }}>
            Custom API Keys (BYOK)
          </Typography>
        </Box>
        <Typography style={{ fontSize: '14px', color: '#64748B', marginBottom: '28px', lineHeight: 1.5 }}>
          Optional: Use your own API provider keys. When set, these will be used instead of the default MCG keys.
        </Typography>

        <Box display="flex" flexDirection="column">
          <KeyRow
            label="Gemini API Key"
            isSet={checkIsSet('google_api_key')}
            value={geminiKey}
            onChange={setGeminiKey}
            onRemove={() => handleRemoveKey('google_api_key')}
            placeholder="AIza..."
            classes={classes}
            loading={loading}
          />

          <KeyRow
            label="Mistral API Key"
            isSet={checkIsSet('mistral_api_key')}
            value={mistralKey}
            onChange={setMistralKey}
            onRemove={() => handleRemoveKey('mistral_api_key')}
            placeholder="mistral-..."
            classes={classes}
            loading={loading}
          />

          <KeyRow
            label="Replicate API Key"
            isSet={checkIsSet('replicate_api_key')}
            value={replicateKey}
            onChange={setReplicateKey}
            onRemove={() => handleRemoveKey('replicate_api_key')}
            placeholder="r8_..."
            classes={classes}
            loading={loading}
          />

          <KeyRow
            label="HeyGen API Key"
            isSet={checkIsSet('heygen_api_key')}
            value={heygenKey}
            onChange={setHeygenKey}
            onRemove={() => handleRemoveKey('heygen_api_key')}
            placeholder="sk-..."
            classes={classes}
            loading={loading}
          />

          <Box mt={1}>
            <Button
              variant="contained"
              color="primary"
              disabled={loading || (!geminiKey && !mistralKey && !replicateKey && !heygenKey)}
              onClick={handleSaveKeys}
              className={classes.button}
              style={{ padding: '12px 32px', minWidth: '160px' }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Save API Keys'}
            </Button>
          </Box>

          <Box mt={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={generateVideo}
                  onChange={handleToggleVideo}
                  color="primary"
                />
              }
              label={
                <Typography style={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>
                  Generate Section Intro Videos
                </Typography>
              }
            />
            <Typography style={{ fontSize: '12px', color: '#64748B', marginLeft: '32px', marginTop: '-4px' }}>
              Requires HeyGen API key. Generates a 1-minute avatar video for each course section.
            </Typography>
          </Box>

          {generateVideo && (
            <Box mt={2} ml={4}>
              <Tooltip
                title={googleDrive.needsReconsent ? 'Reconnect Google Drive to enable backup — your current connection only has read access' : ''}
                placement="top"
              >
                <span>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={backupIntroVideosToDrive}
                        onChange={handleToggleBackup}
                        color="primary"
                        disabled={googleDrive.needsReconsent || !googleDrive.connected}
                      />
                    }
                    label={
                      <Typography style={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>
                        Backup intro videos to Google Drive
                      </Typography>
                    }
                  />
                </span>
              </Tooltip>
              <Typography style={{ fontSize: '12px', color: '#64748B', marginLeft: '32px', marginTop: '-4px' }}>
                Saves each generated section intro video to your connected Google Drive in a folder named '&#123;Learning Path Name&#125; - MCG Course'.
              </Typography>
            </Box>
          )}

          {generateVideo && (
            <Box mt={3}>
              <Typography style={{ fontSize: '14px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                Avatar ID (optional)
              </Typography>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="e.g. 95197579f08d4431a1596c3ef4ed1225"
                value={avatarId || ''}
                onChange={e => onAvatarIdChange && onAvatarIdChange(e.target.value)}
              />
              <Typography style={{ fontSize: '12px', color: '#64748B', marginTop: '6px' }}>
                Force a specific HeyGen avatar for every section. When set, this avatar is used as-is and overrides automatic selection — even a blacklisted avatar is honoured. Leave empty to auto-select from your avatars.
              </Typography>
            </Box>
          )}
        </Box>
      </div>

      {/* Custom Prompts */}
      <div className={classes.section}>
        <Box display="flex" alignItems="center" mb={2.5}>
          <EditIcon style={{ color: '#3B82F6', marginRight: '16px' }} />
          <Typography style={{ fontSize: '16px', fontWeight: 700, color: '#1E293B' }}>
            Custom Prompts
          </Typography>
        </Box>
        <Typography style={{ fontSize: '14px', color: '#64748B', marginBottom: '28px', lineHeight: 1.5 }}>
          Optional: override the prompts the generator uses for video scripts and HeyGen video creation. Leave them on the default unless you have a specific reason to change them.
        </Typography>

        {promptError && (
          <Box mb={2} p={1.5} style={{ background: '#FEE2E2', borderRadius: '4px' }}>
            <Typography style={{ fontSize: '13px', color: '#991B1B' }}>
              {promptError}
            </Typography>
          </Box>
        )}

        <PromptRow
          label="Video Script Generation Rules"
          description="Rules the script-writer LLM follows for each section's narration (length, tone, paragraph style, etc.)."
          value={scriptRulesValue}
          onChange={setScriptRulesValue}
          rows={8}
          isCustom={promptOverrides.video_script_rules != null}
          defaultValue={promptDefaults.video_script_rules}
          onSave={() => handleSavePrompt('custom_video_script_rules', scriptRulesValue, 'script')}
          onReset={() => handleResetPrompt('custom_video_script_rules', 'script')}
          loading={promptLoading.script}
          classes={classes}
        />

        <PromptRow
          label="Video Generation Prompt (HeyGen)"
          description="Instructions sent to HeyGen describing how the avatar video should look and sound. The narration script is appended automatically."
          value={heygenPromptValue}
          onChange={setHeygenPromptValue}
          rows={14}
          isCustom={promptOverrides.heygen_prompt != null}
          defaultValue={promptDefaults.heygen_prompt}
          onSave={() => handleSavePrompt('custom_heygen_prompt', heygenPromptValue, 'heygen')}
          onReset={() => handleResetPrompt('custom_heygen_prompt', 'heygen')}
          loading={promptLoading.heygen}
          classes={classes}
        />
      </div>
    </div>
  )
}

export default withStyles(styles)(SettingsPanel)
